import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const when = (d) => (d ? new Date(d).toLocaleString() : "—");
const BUS = "accounts-changed";
const changed = () => window.dispatchEvent(new Event(BUS));
const useChanged = (fn) => useEffect(() => { window.addEventListener(BUS, fn); return () => window.removeEventListener(BUS, fn); }, [fn]);

const DeleteDialog = ({ u, onClose }) => {
  const [pv, setPv] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/accounts/users/${u.user_id}/preview`).then((r) => setPv(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [u.user_id, onClose]);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/accounts/users/${u.user_id}/delete`, { confirm_name: typed });
      toast.success(`Deleting ${pv.name}. Their photos are being moved to OneDrive TEMP.`);
      changed();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const c = pv?.counts;
  const shared = c ? c.family + c.guest + c.admin : 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle>Delete {pv?.name || u.email}?</DialogTitle>
          <DialogDescription>Files are kept in OneDrive, never deleted.</DialogDescription>
        </DialogHeader>
        {!pv ? <Loader2 className="animate-spin" /> : (
          <div className="text-sm text-neutral-700 space-y-3">
            <ul className="list-disc pl-5 space-y-1" data-testid="delete-account-summary">
              <li><b data-testid="delete-count-private">{c.private}</b> private items stay encrypted in OneDrive <code>TEMP/{pv.name}/Private/</code>. Nobody can view or restore them on the site, including you</li>
              <li><b data-testid="delete-count-shared">{shared}</b> Family/Guest items they uploaded ({c.family} Family, {c.guest} Guest{c.admin ? `, ${c.admin} Admin` : ""}) move to <code>TEMP/{pv.name}/</code> and leave the shared spaces</li>
              <li><b data-testid="delete-count-albums">{c.albums}</b> albums are copied to <code>TEMP/{pv.name}/Albums/</code> and removed from the site</li>
              <li>Their sign-ins end and the account is removed. Photos synced from OneDrive folders are not affected.</li>
            </ul>
            <label className="block">Type <b>{pv.name}</b> to confirm
              <input className="login-input mt-2" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-input" />
            </label>
            <div className="flex gap-2 justify-end">
              <button className="btn-outline !py-1.5 !px-4 text-sm" onClick={onClose} data-testid="delete-cancel-button">Cancel</button>
              <button className="btn-danger !py-1.5 !px-4 text-sm" disabled={busy || typed.trim().toLowerCase() !== pv.name.trim().toLowerCase()} onClick={go} data-testid="delete-confirm-button">
                {busy ? "Starting…" : "Delete account"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DeleteAccountButton = ({ u }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button className="btn-outline !py-1 !px-3 text-xs text-red-700" onClick={() => setOpen(true)} data-testid={`delete-account-${u.user_id}`}><Trash2 size={12} className="inline -mt-0.5" /> Delete</button>
      {open && <DeleteDialog u={u} onClose={close} />}
    </>
  );
};

export const DeletionJobs = ({ onDone }) => {
  const [jobs, setJobs] = useState([]);
  const load = useCallback(() => api.get("/admin/accounts/deletions").then((r) => setJobs((prev) => {
    if (prev.length > r.data.jobs.length) onDone?.();
    return r.data.jobs;
  })), [onDone]);
  useChanged(load);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  if (!jobs.length) return null;
  return (
    <div className="mt-4 space-y-2" data-testid="deletion-jobs">
      {jobs.map((j) => (
        <div key={j.id} className="admin-row" data-testid={`deletion-job-${j.id}`}>
          <div className="text-sm">
            Deleting <b>{j.name}</b> · {j.progress?.done || 0}/{j.progress?.total || 0} files safe in OneDrive
            {j.state === "blocked" && <div className="text-red-700 text-xs mt-1" data-testid={`deletion-error-${j.id}`}>{j.error}. Nothing was removed from the site yet.</div>}
          </div>
          {j.state === "blocked"
            ? <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => api.post(`/admin/accounts/deletions/${j.id}/retry`).then(load)} data-testid={`deletion-retry-${j.id}`}>Retry</button>
            : <Loader2 className="animate-spin" size={16} />}
        </div>
      ))}
    </div>
  );
};

const RestoreState = ({ r }) => {
  if (!r) return null;
  if (r.state === "done") return <span className="text-xs text-green-700"> · restored {r.added} to Admin space</span>;
  if (r.state === "blocked") return <span className="text-xs text-red-700"> · {r.error}</span>;
  return <span className="text-xs text-neutral-500"> · restoring {r.done || 0}/{r.total || "?"}</span>;
};

export const DeletedAccounts = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/accounts/deleted").then((r) => setRows(r.data.accounts)), []);
  useChanged(load);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  if (!rows.length) return null;
  return (
    <div className="mt-8" data-testid="deleted-accounts">
      <h3 className="text-sm font-semibold text-neutral-900 mb-2">Deleted accounts</h3>
      {rows.map((d) => (
        <div key={d.id} className="admin-row" data-testid={`deleted-account-${d.id}`}>
          <div className="min-w-0 text-sm">
            <div>{d.name} <span className="text-neutral-500">· {d.email}</span></div>
            <div className="text-xs text-neutral-500 mt-0.5">
              Deleted {when(d.deleted_at)} by {d.deleted_by} · {d.counts.private} private, {d.counts.family} Family, {d.counts.guest} Guest, {d.counts.albums} albums
              {d.temp_path && <> · OneDrive: <code data-testid={`deleted-temp-path-${d.id}`}>{d.temp_path}</code></>}
              <RestoreState r={d.restore} />
              {d.blocked && <span className="text-xs text-red-700" data-testid={`deleted-blocked-${d.id}`}> · Blocked</span>}
            </div>
          </div>
          <div className="flex gap-2">
          {d.blocked && (
            <button className="btn-outline !py-1 !px-3 text-xs" data-testid={`deleted-unblock-${d.id}`}
              onClick={() => window.confirm(`Unblock ${d.email}? They could sign up again.`) && api.post(`/admin/accounts/deleted/${d.id}/unblock`).then(load)}>Unblock</button>
          )}
          {d.temp_path && (!d.restore || d.restore.state === "blocked") && (
            <button className="btn-outline !py-1 !px-3 text-xs" data-testid={`restore-photos-${d.id}`}
              onClick={() => window.confirm(`Restore ${d.name}'s Family and Guest photos into your Admin space?\n\nTheir Private photos stay encrypted in OneDrive TEMP and are never restored on the site.`) && api.post(`/admin/accounts/deleted/${d.id}/restore`).then(load)}>
              Restore Family/Guest photos
            </button>
          )}
          </div>
        </div>
      ))}
    </div>
  );
};

export const ManageAccountsCard = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data.users)), []);
  useEffect(() => { if (me?.role === "admin") load(); }, [load, me?.role]);
  if (!users || me?.role !== "admin") return null;
  return (
    <section className="settings-card" data-testid="manage-accounts-card">
      <h2 className="text-base font-semibold text-neutral-900">Manage accounts</h2>
      <p className="text-sm text-neutral-500 mb-3">Deleting an account moves that person's photos to OneDrive (TEMP) and removes them from the site.</p>
      <div className="admin-table">
        {users.filter((u) => u.user_id !== me.user_id).map((u) => (
          <div className="admin-row" key={u.user_id} data-testid={`manage-account-row-${u.user_id}`}>
            <div className="text-sm">{u.name} <span className="text-neutral-500">· {u.email} · {u.role}</span></div>
            <DeleteAccountButton u={u} />
          </div>
        ))}
      </div>
      <DeletionJobs onDone={load} />
      <DeletedAccounts />
    </section>
  );
};
